import React from "react";
import { Link } from "react-router-dom";
import {
  Ruler,
  Fish,
  Users,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import {
  images,
  club,
  closedSeasons,
  alwaysProtected,
  guestRules,
  guestWaters,
} from "../mock/mock";

function Block({ icon: Icon, eyebrow, title, children }) {
  return (
    <section className="py-16 border-t border-border first:border-t-0">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div>
          {eyebrow && (
            <span className="block text-accent text-xs font-600 uppercase tracking-[0.25em]">
              {eyebrow}
            </span>
          )}
          <h2 className="font-serif text-3xl font-600 text-foreground leading-tight">
            {title}
          </h2>
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Bestimmungen() {
  return (
    <div>
      <PageHero
        eyebrow="Erlaubnisschein 2026"
        title="Bestimmungen"
        subtitle="Schonzeiten und die wichtigsten Regeln unseres Vereins auf einen Blick."
        image={images.anglerAutumn}
      />

      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        {/* Schonzeiten */}
        <Block icon={Ruler} eyebrow="Schutz" title="Schonmaße & Schonzeiten">
          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left">
              <thead className="bg-secondary">
                <tr>
                  <th className="px-5 py-3 text-sm font-600 text-foreground">Fischart</th>
                  <th className="px-5 py-3 text-sm font-600 text-foreground">Schonmaß</th>
                  <th className="px-5 py-3 text-sm font-600 text-foreground">Schonzeit</th>
                </tr>
              </thead>
              <tbody>
                {closedSeasons.map((s, i) => (
                  <tr key={s.fish} className={i % 2 ? "bg-card" : "bg-background"}>
                    <td className="px-5 py-3 text-sm font-500 text-foreground">{s.fish}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{s.size}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{s.season}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{alwaysProtected}</p>
          <p className="mt-2 text-xs text-muted-foreground italic">{club.permit.note}</p>
        </Block>

        {/* Gastregelung */}
        <Block icon={Users} eyebrow="Gäste" title="Einen Gast mitnehmen">
          <ul className="space-y-3">
            {guestRules.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-muted-foreground">
                <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-700 flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 bg-secondary/60 rounded-2xl p-6 border border-border">
            <h3 className="font-600 text-foreground mb-3">
              Tageskarten ohne Mitglieds-Begleitung nur an:
            </h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {guestWaters.map((w) => (
                <div key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Fish className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  {w}
                </div>
              ))}
            </div>
          </div>
        </Block>
      </div>

      {/* CTA */}
      <section className="py-16 bg-secondary/60 border-t border-border">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif text-3xl font-600 text-foreground">Fragen zu den Bestimmungen?</h2>
          <p className="mt-3 text-muted-foreground text-lg">
            Wendet euch gerne an unseren Vorstand oder kauft eure Tageskarte online.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={club.hejfish} target="_blank" rel="noopener noreferrer">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2">
                Angelkarten online <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
            <Link to="/kontakt">
              <Button variant="outline" className="border-primary/30 text-primary hover:bg-secondary font-500 gap-2">
                Kontakt <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
